import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Alert, Button, Typography, type TypographyProps } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    isBarcodeIssueKind,
    type BarcodeLabels,
    type IssueEntry,
    type IssueKind,
} from '../../importIssues';
import { IssueCard } from './IssueCard';
import { labelsSummaryText } from './labelsSummary';

type IssuesSummaryProps = {
    /** The kinds the last check found, in the summary's order. */
    issues: IssueEntry[];
    /**
     * The labels notice (`labelsNoticeOf`): the generated barcodes and the
     * labels they need; `null` shows none.
     */
    labels: BarcodeLabels | null;
    /** Rows that need attention and are not excluded: the heading's count. */
    needsAttentionCount: number;
    /** The kind the table is filtered to; `null` for none. */
    issueFilter: IssueKind | null;
    /** The fixes wait (a check or the import is running). */
    isFixDisabled: boolean;
    /** A bulk action can be undone. */
    canUndo: boolean;
    onApplyFix: (kind: IssueKind) => void;
    /** Shows only a kind's rows, or every row again with `null`. */
    onShowIssueRows: (kind: IssueKind | null) => void;
    /** Undoes the most recent bulk action. */
    onUndo: () => void;
};

// The summary is a second-level heading under the step's title.
const asH2: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h2 {...domProps}>{children}</h2>;

/**
 * The Products step's issues summary (bos-app#213, US-02): over the tabs,
 * how many rows need attention and what to do, **Undo last change** (the
 * most recent bulk action, until the import), and one card per kind of
 * problem the last check found (`IssueCard`), each with its count, the
 * default its bulk fix applies, the fix and Show rows.
 *
 * With a barcode case (bos-app#232) the line under the heading says the
 * old system's codes can't be scanned as barcodes, and a notice over the
 * cards says generated barcodes need printed labels, with "N new barcodes:
 * M labels to print" once the last check counts some (`labels`). The
 * notice stays while the check says the import generates barcodes.
 *
 * Nothing when the last check found no kind, generates no barcode and
 * nothing can be undone; it stays while an action can be undone, so the
 * last fix that clears every kind can still be taken back.
 */
export const IssuesSummary: React.FC<IssuesSummaryProps> = ({
    issues,
    labels,
    needsAttentionCount,
    issueFilter,
    isFixDisabled,
    canUndo,
    onApplyFix,
    onShowIssueRows,
    onUndo,
}) => {
    const { t } = useTranslation('pages');
    const headingId = React.useId();

    if (issues.length === 0 && !canUndo && labels === null) {
        return null;
    }
    const hasBarcodeIssues = issues.some((entry) =>
        isBarcodeIssueKind(entry.kind),
    );

    return (
        <section
            aria-labelledby={headingId}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('catalog-import-issues')}
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <Typography id={headingId} render={asH2} type="h4">
                        {needsAttentionCount > 0
                            ? t('catalogImport.products.bar.needsAttention', {
                                  count: needsAttentionCount,
                                  rows: countParam(needsAttentionCount),
                              })
                            : t('catalogImport.products.bar.allClear')}
                    </Typography>
                    {issues.length === 0 ? null : (
                        <Typography color="muted" type="body-sm">
                            {t(
                                hasBarcodeIssues
                                    ? 'catalogImport.products.issues.barcodesIntro'
                                    : 'catalogImport.products.issues.intro',
                            )}
                        </Typography>
                    )}
                </div>
                <Button
                    className="shrink-0"
                    isDisabled={!canUndo}
                    onPress={onUndo}
                    size="sm"
                    variant="tertiary"
                    {...getNodeTestIdProp('catalog-import-issues-undo')}
                >
                    <Icon aria-hidden icon="gravity-ui:arrow-rotate-left" />
                    {t('catalogImport.products.issues.undo')}
                </Button>
            </div>
            {labels === null ? null : (
                <Alert
                    status="accent"
                    {...getNodeTestIdProp('catalog-import-issues-labels')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('catalogImport.products.issues.labels.title')}
                        </Alert.Title>
                        <Alert.Description>
                            {t(
                                'catalogImport.products.issues.labels.description',
                            )}
                        </Alert.Description>
                        {labels.barcodes > 0 ? (
                            <Alert.Description
                                {...getNodeTestIdProp(
                                    'catalog-import-issues-labels-count',
                                )}
                            >
                                <strong>{labelsSummaryText(t, labels)}</strong>
                            </Alert.Description>
                        ) : null}
                    </Alert.Content>
                </Alert>
            )}
            {issues.length === 0 ? null : (
                <ul
                    aria-label={t('catalogImport.products.issues.ariaLabel')}
                    className="grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-4"
                >
                    {issues.map((entry) => (
                        <li className="flex min-w-0" key={entry.kind}>
                            <IssueCard
                                entry={entry}
                                isFiltered={issueFilter === entry.kind}
                                isFixDisabled={isFixDisabled}
                                onApplyFix={onApplyFix}
                                onShowRows={onShowIssueRows}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};
